import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  IDashcam,
  IFilter,
  IQueryParam,
  IReporteGenerico,
  IReporteTracker4G,
} from 'modelos/src';
import { Subscription, retry, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import {
  EstadoConexion,
  duracion,
  estadoConexion,
  haceCuanto,
  minutosDesde,
} from '../../../../../auxiliares/estado-conexion.util';
import { MarkerMapa } from '../../../../../auxiliares/mapa-facil/interfaces';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { SimsService } from '../../../../../auxiliares/servicios/http/sims.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { rutaDetalleEntidad } from '../../../../../auxiliares/servicios/rutas-entidades';
import { BOTONES } from '../../../../../componentes/botones';
import {
  PIEZAS_DETALLE,
  SimDetalle,
} from '../../../../../componentes/detalle/detalle.components';
import {
  MapCardComponent,
  MapCardConfig,
} from '../../../../../componentes/map-card/map-card.component';
import { TIPO_REPORTE_DASHCAM } from '../listado-dashcams/listado-dashcams.component';

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaDashcam {
  conexion: EstadoConexion;
  ultimoReporte?: string;
  hace?: string;
  asignadaA?: string;
  rutaActivo?: string[];
  marcaModelo?: string;
  protocoloModelo?: string;
  uniqueId?: string;
  numeroSerie?: string;
  estadoCuenta?: string;
  /** frecReporte (minutos) legible: "5 min", "1 h". */
  frecuencia?: string;
  equipo: {
    fabricante?: string;
    modelo?: string;
    imei?: string;
    idTerminal?: string;
    firmware?: string;
    versionProtocolo?: string;
    registro?: string;
  };
  canales: { titulo: string; detalle: string; habilitado: boolean }[];
  flujoEnVivo?: string;
  corteEnVivo?: string;
  evidencias: string;
  sims: SimDetalle[];
}

/**
 * Pestaña Info del detalle de dashcam. Recibe la dashcam ya populada desde la
 * página (que la refresca cada 30 s) y trae aparte su último reporte de
 * posición para el estado de conexión y el mapa.
 */
@Component({
  selector: 'app-detalles-dashcam-info',
  standalone: true,
  imports: [
    AuxiliaresModule,
    BOTONES,
    MapCardComponent,
    RouterLink,
    ...PIEZAS_DETALLE,
  ],
  templateUrl: './detalles-dashcam-info.component.html',
  styleUrl: './detalles-dashcam-info.component.scss',
})
export class DetallesDashcamInfoComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private simsService = inject(SimsService);
  private mapaService = inject(MapaFacilService);

  private _dashcam?: IDashcam;
  @Input({ required: true }) set dashcam(valor: IDashcam | undefined) {
    this._dashcam = valor;
    this.armarVista();
  }
  get dashcam(): IDashcam | undefined {
    return this._dashcam;
  }

  public vista?: VistaDashcam;
  private reporte?: IReporteGenerico;
  private reporte$?: Subscription;
  public loadingMap = signal(true);
  public mapCardConfig: MapCardConfig = {
    title: 'Última ubicación',
    icon: 'location_on',
    color: 'red',
  };

  /** Igual que en tracker: en pantallas anchas el mapa llena su columna. */
  public pantallaAncha = signal(window.innerWidth > 900);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('contenedorMapa') set contenedorMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 900);
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama desde los dos caminos de
   * carga (dashcam y último reporte), que terminan en cualquier orden.
   */
  private armarVista(): void {
    const d = this._dashcam;
    if (!d) return;
    const fecha =
      this.reporte?.valores?.timestamp ?? this.reporte?.fechaCreacion;
    const e = d.equipo;
    const duracionEnVivo = d.configVideo?.duracionMaximaEnVivo;

    this.vista = {
      conexion: estadoConexion(minutosDesde(fecha), d.frecReporte),
      ultimoReporte: fecha ? HelperService.getFechaYHora(fecha) : undefined,
      hace: fecha ? haceCuanto(fecha).toLowerCase() : undefined,
      asignadaA: d.activo?.identificacion,
      rutaActivo: d.activo
        ? rutaDetalleEntidad('Activo', d.activo._id, d.activo.categoria)
        : undefined,
      marcaModelo:
        [d.modelo?.marca, d.modelo?.modelo].filter(Boolean).join(' · ') ||
        undefined,
      protocoloModelo: d.modelo
        ? d.modelo.protocoloDashcam?.toUpperCase() || 'No declarado'
        : undefined,
      uniqueId: d.uniqueId,
      numeroSerie: d.numeroSerie,
      estadoCuenta: d.estadoCuenta ? String(d.estadoCuenta) : undefined,
      frecuencia: d.frecReporte ? duracion(d.frecReporte) : undefined,
      equipo: {
        fabricante: e?.fabricante,
        modelo: e?.modelo,
        imei: e?.imei,
        idTerminal: e?.idTerminal,
        firmware: e?.versionFirmware,
        versionProtocolo: e?.versionProtocolo,
        registro: e?.fechaRegistro
          ? `${HelperService.getFechaYHora(e.fechaRegistro)} (hace ${haceCuanto(e.fechaRegistro).toLowerCase()})`
          : undefined,
      },
      canales: (d.canales ?? []).map((c) => ({
        titulo: `Canal ${c.numero ?? '?'}${c.nombre ? ' · ' + c.nombre : ''}`,
        detalle: [c.funcion, c.audio ? 'con audio' : 'sin audio']
          .filter(Boolean)
          .join(' · '),
        habilitado: c.habilitado !== false,
      })),
      flujoEnVivo: d.configVideo?.flujoEnVivo,
      corteEnVivo: duracionEnVivo
        ? duracion(Math.round(duracionEnVivo / 60))
        : undefined,
      evidencias: d.configVideo?.solicitarEvidencia ? 'Sí' : 'No',
      sims: [
        this.simsService.simDetalle(1, d.sim1, d.idSim1),
        this.simsService.simDetalle(2, d.sim2, d.idSim2),
      ],
    };
  }

  private dibujarMapa(): void {
    const valores = this.reporte?.valores as IReporteTracker4G | undefined;
    const coordenadas = valores?.geojson?.coordinates as
      | [number, number]
      | undefined;
    if (!coordenadas) {
      this.loadingMap.set(false);
      return;
    }
    const marker: MarkerMapa = {
      label: 'puntoSimple',
      coor: coordenadas,
      centrar: true,
    };
    this.mapCardConfig = {
      // La fecha del reporte ya está en el resumen.
      title: 'Última ubicación',
      subtitle: HelperService.extraerDireccion((valores as any)?.direccion),
      icon: 'location_on',
      color: 'red',
      mapOptions: { markers: [marker], center: coordenadas, zoom: 15 },
    };
    this.mapaService.updateData({ markers: [marker], zoom: 15 });
    this.loadingMap.set(false);
  }

  /** Último reporte de posición ('Dashcam 4G') de esta dashcam. */
  private listarUltimoReporte(): void {
    const id = this._dashcam?._id;
    if (!id) {
      this.loadingMap.set(false);
      return;
    }
    const filter: IFilter<IReporteGenerico> = {
      idEntidad: id,
      tipoReporte: TIPO_REPORTE_DASHCAM,
    } as IFilter<IReporteGenerico>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reporte$?.unsubscribe();
    this.reporte$ = this.lista2
      .ultimosReportesGenericos(query, 30)
      // Un request que falla terminaba el refresco para siempre.
      .pipe(retry({ delay: () => timer(30000) }))
      .subscribe((data) => {
        this.reporte = data.datos[0] as IReporteGenerico | undefined;
        this.dibujarMapa();
        this.armarVista();
      });
  }

  ngOnInit(): void {
    this.listarUltimoReporte();
  }

  ngOnDestroy(): void {
    this.observadorMapa?.disconnect();
    this.reporte$?.unsubscribe();
    this.mapaService.clearMap();
  }
}
